import { WhitePaperFilter, paperRemoval } from "./WhitePaperFilter";
import { HTMLContainer, useEditor } from "./canvas";
import { useValue } from "@tldraw/state-react";
import type { TLImageShape } from "@tldraw/tlschema";
import type { ReactNode } from "react";
// Off-screen images unmount their DOM, releasing decoded surfaces. Half a screen
// of margin stops zooming from remounting images at the edges; it scales with the
// screen, so deep zoom still releases large images nearby.
function VisibleImage({
  shape,
  children,
}: {
  shape: TLImageShape;
  children: ReactNode;
}) {
  const editor = useEditor();
  const visible = useValue(
    "image visibility",
    () => {
      const view = editor.getViewportPageBounds(),
        m = Math.max(view.w, view.h) / 2;
      return (
        editor.getShapePageBounds(shape.id)?.collides({
          x: view.x - m,
          y: view.y - m,
          w: view.w + m * 2,
          h: view.h + m * 2,
        }) ?? false
      );
    },
    [editor, shape.id],
  );
  const status = useValue(
    "image upload status",
    () => {
      const asset = shape.props.assetId && editor.getAsset(shape.props.assetId);
      return asset && !asset.props.src ? asset.meta.imageUpload : null;
    },
    [editor, shape.props.assetId],
  );
  return visible ? (
    <>
      {children}
      {status && (
        <HTMLContainer
          style={{
            width: shape.props.w,
            height: shape.props.h,
            pointerEvents: "none",
          }}
        >
          <span className="image-upload-badge">
            {status === "failed" ? "Upload paused" : "Adding image…"}
          </span>
        </HTMLContainer>
      )}
    </>
  ) : null;
}
/** Planroom's image presentation (paper removal, mask, border) around an image body. */
export function BoardImageFrame({
  shape,
  children,
}: {
  shape: TLImageShape;
  children: ReactNode;
}) {
  const strength = paperRemoval(shape.meta.paperRemoval),
    filterId = `paper-${shape.id.replaceAll(":", "-")}`;
  const radius =
    shape.meta.mask === "ellipse"
      ? "50%"
      : shape.meta.mask === "round"
        ? "24px"
        : 0;
  const border = Math.max(0, Math.min(20, Number(shape.meta.borderWidth) || 0));
  const color = /^#[\da-f]{6}$/i.test(String(shape.meta.borderColor))
    ? String(shape.meta.borderColor)
    : "#25343b";
  return (
    <VisibleImage shape={shape}>
      {!!strength && (
        <svg width="0" height="0" aria-hidden="true">
          <defs>
            <WhitePaperFilter id={filterId} strength={strength} />
          </defs>
        </svg>
      )}
      <HTMLContainer
        style={{
          width: shape.props.w,
          height: shape.props.h,
          overflow: "hidden",
          borderRadius: radius,
        }}
      >
        <div
          style={{
            width: shape.props.w,
            height: shape.props.h,
            filter: strength ? `url(#${filterId})` : undefined,
          }}
        >
          {children}
        </div>
      </HTMLContainer>
      {border > 0 && (
        <HTMLContainer
          style={{
            width: shape.props.w,
            height: shape.props.h,
            border: `${border}px solid ${color}`,
            borderRadius: radius,
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        />
      )}
    </VisibleImage>
  );
}

/** Board image export: the image with its mask, white-paper filter and border. */
export function boardImageSvg(shape: TLImageShape, image: ReactNode) {
  const strength = paperRemoval(shape.meta.paperRemoval),
    filterId = `paper-export-${shape.id.replaceAll(":", "-")}`;
  const id = `mask-${shape.id.replaceAll(":", "-")}`;
  const border = Math.max(
    0,
    Math.min(20, Number(shape.meta.borderWidth) || 0),
  );
  const color = /^#[\da-f]{6}$/i.test(String(shape.meta.borderColor))
    ? String(shape.meta.borderColor)
    : "#25343b";
  const path =
    shape.meta.mask === "ellipse" ? (
      <ellipse
        cx={shape.props.w / 2}
        cy={shape.props.h / 2}
        rx={shape.props.w / 2}
        ry={shape.props.h / 2}
      />
    ) : (
      <rect
        width={shape.props.w}
        height={shape.props.h}
        rx={shape.meta.mask === "round" ? 24 : 0}
      />
    );
  return (
    <g>
      <defs>
        {!!strength && <WhitePaperFilter id={filterId} strength={strength} />}
        <clipPath id={id}>{path}</clipPath>
      </defs>
      <g clipPath={`url(#${id})`}>
        <g filter={strength ? `url(#${filterId})` : undefined}>{image}</g>
        <g fill="none" stroke={color} strokeWidth={border * 2}>
          {border > 0 && path}
        </g>
      </g>
    </g>
  );
}
